import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import _get from 'lodash/get';
import useDetectPrint from 'react-detect-print';

import Adhesion from '../../../atoms/adhesion';
import CardResource from '../../../molecules/profile/card-resource';
import CardResourceContainer from '../../../molecules/profile/card-resource-container';
import FinancialStrengthRatings from '../../../molecules/profile/financial-strength-ratings';
import FooterDisclosures from '../../../atoms/footer-disclosures';
import HeaderQuoteBar from '../../../molecules/profile/header-quote-bar';
import HistoricalRatesOfReturn from '../../../molecules/profile/historical-rates-of-return/index';
import IncomePayoutRates from '../../../molecules/profile/income-payout-rates';
import IncomeWithdrawalOptions from '../../../molecules/profile/income-withdrawal-options';
import InterestCreditingRates from '../../../molecules/profile/interest-crediting-rates';
import InvestmentFacts from '../../../molecules/profile/investment-facts';
import InvestmentStrategy from '../../../molecules/profile/investment-strategy';
import PageHeader from '../../../molecules/profile/page-header';
import PageMain from '../../../molecules/profile/page-main';
import PageSideRail, { DisclosurePrint } from '../../../molecules/profile/page-side-rail';
import RisksDisclosure from '../../../molecules/profile/risks-disclosure';
import SectionDivider from '../../../atoms/section-divider';
import sectionDividerOptions from '../../../../helpers/statics/section-divider-options';
import transformInvestmentFacts from '../../../../stores/profile/modules/transform-investment-facts';
import useStore from '../../../../stores/profile/use-fixed-annuity-tiaa-traditional-store';
import { className } from '../../../../utils/GlobalCssProxy';
import { getIsDynamicGreetingShown } from '../../../molecules/profile/dynamic-greeting/dynamic-greeting-helper';

const FixedAnnuityTiaaTraditional = () => {
  const store = useStore();
  const quoteData = store.quote.isLoaded ? store.quote.data : {};
  const xrefData = store.xref.isLoaded ? store.xref.data : {};
  const participantHoldingsData =
    store.participantHoldings && store.participantHoldings.isLoaded
      ? store.participantHoldings.data
      : {};
  const showDynamicGreeting =
    participantHoldingsData && getIsDynamicGreetingShown(participantHoldingsData);
  const printing = useDetectPrint();
  const disclosureList = store.disclosureList.isLoaded ? store.disclosureList.data : [];
  const financialComponent = [
    disclosureList.find(item => {
      return item?.component === 'financial-strength-ratings';
    })
  ];

  return (
    <div data-template="FixedAnnuityTiaaTraditional">
      <PageHeader
        displayAsFundType="Fixed Annuity"
        sectionNavOptions={[
          sectionDividerOptions.performance,
          sectionDividerOptions.characteristics,
          sectionDividerOptions.resources
        ]}
      >
        <HeaderQuoteBar>
          <HeaderQuoteBar.CurrentRate
            value={quoteData.currentCreditingRate}
            showAsOf={false} // TIAA-10377 Suppress as of date for TIAA traditional annuities only
          />
          <HeaderQuoteBar.GuaranteedRate value={quoteData.guaranteedCreditingRate} />
          <HeaderQuoteBar.AccountInceptionDate
            asOfDate={quoteData.inceptionDate}
            responsive={printing ? undefined : 1}
          />
          <HeaderQuoteBar.AssetClass responsive={printing ? undefined : 2}>
            {quoteData.assetClass}
          </HeaderQuoteBar.AssetClass>
        </HeaderQuoteBar>
      </PageHeader>
      <PageMain>
        <SectionDivider
          labelText={sectionDividerOptions.performance.label}
          id={sectionDividerOptions.performance.id}
          isDynamicGreetingShown={showDynamicGreeting}
          dynamicGreetingParticipantHoldings={participantHoldingsData}
        >
          <Row>
            <Col>
              <HistoricalRatesOfReturn isTraditional={true} />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FinancialStrengthRatings isPrinting={printing} />
            </Col>
            <Col md={6} {...className('d-none', 'd-md-block')}>
              <Adhesion.Performance />
            </Col>
          </Row>
          <Row>
            <Col>
              <DisclosurePrint disclosureList={financialComponent} avoidPageBreak />
            </Col>
          </Row>
          <Row>
            <Col>
              <InterestCreditingRates {...store.interestCreditingRates} />
            </Col>
          </Row>
          <Row>
            <Col>
              <RisksDisclosure />
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.characteristics.label}
          id={sectionDividerOptions.characteristics.id}
        >
          <Row>
            <Col>
              <IncomePayoutRates
                asOfDate={_get(store, 'incomePayoutRates.data.asOfDate')}
                incomePayoutRates={_get(store, 'incomePayoutRates.data.items')}
              />
            </Col>
          </Row>
          <Row>
            <Col {...className('d-none', 'd-md-block')} md={8}>
              <Adhesion.Characteristics />
            </Col>
            <Col md={4}>
              <InvestmentStrategy
                heading="Account Description"
                {...(store.investmentStrategy.data || {})}
              >
                {store.investmentStrategy.isLoaded
                  ? store.investmentStrategy.data.investmentStrategy
                  : ''}
              </InvestmentStrategy>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <IncomeWithdrawalOptions {..._get(store, 'incomeWithdrawalOptions.data', {})} />
            </Col>
            <Col md={6}>
              <InvestmentFacts
                header="Account Facts"
                facts={
                  store.investmentFacts.isLoaded
                    ? transformInvestmentFacts(
                      store.investmentFacts.data,
                      store.fundType,
                      store.assetClass
                    )
                    : [] // prettier-ignore
                }
                ticker={xrefData.symbol ? xrefData.symbol : null}
              ></InvestmentFacts>
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.resources.label}
          id={sectionDividerOptions.resources.id}
        >
          <CardResourceContainer>
            <CardResource.SimpleLink
              bodyText="TIAA Traditional Annuities"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Learn"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/retire/financial-products/annuities/retirement-plan-annuities/tiaa-traditional-annuity"
              iconVariant="Learn"
              titleText="Learn"
            />
            <CardResource.SimpleLink
              bodyText="Lifetime Income Calculator"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Tools"
              data-analytics-section="component"
              href="https://shared.tiaa.org/public/publictools/targetincome/annuityEstimator"
              iconVariant="Calculator"
              titleText="Tools"
            />
            <CardResource.SimpleLink
              bodyText="Retirement Planner"
              href="https://shared.tiaa.org/private/digitalinvestadvice/experience/personalgoals/userlanding"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Planning"
              data-analytics-section="component"
              iconVariant="Planning"
              titleText="Planning"
            />
            <CardResource.SimpleLink
              bodyText="Investment Performance"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Research"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/investment-performance"
              iconVariant="Research"
              titleText="Research"
            />
          </CardResourceContainer>
        </SectionDivider>
        <FooterDisclosures />
        <div {...className('admaster-number', 'd-print-none')}>X-3240775P-Q0923W</div>
        <DisclosurePrint disclosureList={disclosureList} admasterNumber="X-3240775P-Q0923W" />
      </PageMain>
      <PageSideRail />
    </div>
  );
};

export default FixedAnnuityTiaaTraditional;
